'use client';

import { CustomerRatingsProductInfoFragmentDoc } from '@packages/gql/generated/shopping/CustomerRatingsProductInfoFragmentDoc';
import type { FragmentType } from '@packages/gql/generated/shopping/fragment-masking';
import { getFragmentData } from '@packages/gql/generated/shopping/fragment-masking';
import { Button } from '@packages/shared/src/components/Button';
import { Divider } from '@packages/shared/src/components/Divider';
import { Grid } from '@packages/shared/src/components/Grid/Grid';
import { Link } from '@packages/shared/src/components/Link/Link';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { ArrowMediumLeft32 } from '@packages/themes/icons';
import { useSearchParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { CompactProduct } from '../CompactProduct/CompactProduct';
import { ProductOverview } from '../CustomerRatings/ProductOverview';
import { RatingInfo } from '../CustomerRatingsV2/RatingInfo/RatingInfo';
import { RatingTags } from '../CustomerRatings/RatingTags';
import { RatingsOverview } from '../CustomerRatingsV2/RatingsOverview/RatingsOverview';

/* GraphQL */ `
  fragment CustomerRatingsProductInfo on Product {
    akl
    name
    slug
    ratingPageSeoRating: seoRating(locale: $locale) {
      product {
        avgRating
        reviewCount
        tags {
          ...RatingTagsFragment
        }
        ...RatingsOverviewFragment
      }
    }
    ...CompactProductFragment
    ...ProductOverviewFragment
    ...RatingTagSkuFragment
  }
`;

export type CustomerRatingsProductInfoProps = {
  productData: FragmentType<typeof CustomerRatingsProductInfoFragmentDoc>;
};

export const CustomerRatingsProductInfo = ({ productData }: CustomerRatingsProductInfoProps) => {
  const product = getFragmentData(CustomerRatingsProductInfoFragmentDoc, productData);
  const { ratingPageSeoRating } = product;
  const { product: ratingProduct } = { ...ratingPageSeoRating };

  const { isMobile } = useDeviceType();

  const { formatMessage } = useIntl();
  const { language, localizeUrl } = useI18n();

  const searchParams = useSearchParams();

  const backToProductLink = localizeUrl(
    `/p/${product.akl}${searchParams && searchParams.size > 0 ? `?${searchParams}` : ''}`,
    language,
  );

  return (
    <>
      {/* TODO: improve styling */}
      <Grid item xs={12} sx={{ paddingTop: '0px !important' }}>
        <Button
          startIcon={<ArrowMediumLeft32 />}
          variant="text"
          sx={{ p: 0 }}
          LinkComponent={Link}
          href={backToProductLink}
        >
          {formatMessage({
            defaultMessage: 'Zurück zum Produkt',
            id: 'customer-ratings.back-to-product',
          })}
        </Button>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <p className="text-size-h5 md:text-size-h3 mb-3 flex items-center gap-1">
          {formatMessage({
            defaultMessage: 'Alle Bewertungen zum Produkt',
            id: 'customer-ratings.overview.headline',
          })}
          {ratingPageSeoRating && <RatingInfo />}
        </p>
        {!isMobile ? (
          <Link
            href={backToProductLink}
            underline="none"
            sx={{ color: 'black', '&:hover': { color: 'primary.main' } }}
          >
            <h1 className="text-size-h4 line-clamp-2 font-semibold">{product.name}</h1>
          </Link>
        ) : (
          <CompactProduct data={product} withProductName showTaxHint={false} />
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        {ratingProduct && ratingPageSeoRating && ratingPageSeoRating.product && (
          <>
            <RatingsOverview data={ratingPageSeoRating.product} />
            <RatingTags ratingTagsData={ratingProduct.tags} skuData={product} />
          </>
        )}
      </Grid>
      {!isMobile && (
        <Grid item xs={12} md={6}>
          <ProductOverview productData={product} />
        </Grid>
      )}
    </>
  );
};
